import { useMemo, useState } from "react";
import { I } from "../Icons.jsx";
import Btn from "../ui/Btn.jsx";
import Display from "../ui/Display.jsx";
import Eyebrow from "../ui/Eyebrow.jsx";
import IconBtn from "../ui/IconBtn.jsx";
import { inputStyle } from "../ui/styles.js";
import SeatingDiagram from "./SeatingDiagram.jsx";

// Opened from GroupCard for dinner/lunch activities. Lets the user pick a
// table shape (rectangular + end counts, or circular) and the seat-1 slot.
// Saves the chosen config back via onSave; passing null clears it.

export default function SeatingModal({ group, people, activity, onSave, onClose }) {
  const n = group.people.length;
  const initial = group.seatingConfig || null;
  const [shape, setShape] = useState(initial?.shape || "rect");
  const [leftEnd, setLeftEnd] = useState(initial?.leftEnd ?? (shape === "rect" ? 1 : 0));
  const [rightEnd, setRightEnd] = useState(initial?.rightEnd ?? (shape === "rect" ? 1 : 0));
  const [seat1Index, setSeat1Index] = useState(initial?.seat1Index ?? 0);
  const [busy, setBusy] = useState(false);

  const peopleMap = useMemo(() => new Map(people.map(p => [p.id, p])), [people]);

  const rectInvalid =
    shape === "rect" && (leftEnd + rightEnd > n || leftEnd < 0 || rightEnd < 0);

  const clampedSeat1 = Math.min(seat1Index, Math.max(0, n - 1));

  const config = rectInvalid || n === 0
    ? null
    : shape === "circular"
      ? { shape: "circular", leftEnd: 0, rightEnd: 0, seat1Index: clampedSeat1 }
      : { shape: "rect", leftEnd, rightEnd, seat1Index: clampedSeat1 };

  const save = async () => {
    setBusy(true);
    try { await onSave(config); onClose(); }
    finally { setBusy(false); }
  };

  const clear = async () => {
    setBusy(true);
    try { await onSave(null); onClose(); }
    finally { setBusy(false); }
  };

  // When user clicks a seat in the preview, rotate the seat1Index so that the
  // clicked geometric slot becomes seat 1 (i.e. holds people[0]).
  const onSeatClick = (slotIdx) => {
    // person at slot s = (s - seat1Index + n) % n
    // we want person 0 at slot = slotIdx, so seat1Index = slotIdx
    setSeat1Index(slotIdx);
  };

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, zIndex: 1000,
      background: "rgba(34,28,20,0.4)", backdropFilter: "blur(4px)",
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "var(--paper-0)", borderRadius: 14, padding: 28,
        width: "94%", maxWidth: 760, maxHeight: "92vh", overflow: "auto",
        border: "1px solid var(--line)", boxShadow: "0 24px 64px rgba(34,28,20,0.25)",
        color: "var(--ink)",
      }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 14, marginBottom: 18 }}>
          <div style={{ flex: 1 }}>
            <Eyebrow>{activity?.name || "Activity"} · seating</Eyebrow>
            <Display size={24} style={{ marginTop: 8 }}>{group.name}</Display>
            <div style={{ fontSize: 13, color: "var(--ink-2)", marginTop: 6 }}>
              {n} {n === 1 ? "person" : "people"} in group order. Pick a shape and click a chair to place seat 1; everyone else fills in clockwise.
            </div>
          </div>
          <IconBtn icon={I.x} title="Close" size={14} onClick={onClose} />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "220px 1fr", gap: 24 }}>
          {/* Controls */}
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div>
              <Eyebrow>Shape</Eyebrow>
              <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                <ShapeOption label="Rectangular" active={shape === "rect"} onClick={() => setShape("rect")} />
                <ShapeOption label="Circular" active={shape === "circular"} onClick={() => setShape("circular")} />
              </div>
            </div>

            {shape === "rect" && (
              <>
                <div>
                  <Eyebrow>Left end</Eyebrow>
                  <input
                    type="number"
                    min={0}
                    max={n}
                    value={leftEnd}
                    onChange={(e) => setLeftEnd(Math.max(0, parseInt(e.target.value) || 0))}
                    style={{ ...inputStyle, marginTop: 8 }}
                  />
                </div>
                <div>
                  <Eyebrow>Right end</Eyebrow>
                  <input
                    type="number"
                    min={0}
                    max={n}
                    value={rightEnd}
                    onChange={(e) => setRightEnd(Math.max(0, parseInt(e.target.value) || 0))}
                    style={{ ...inputStyle, marginTop: 8 }}
                  />
                </div>
                <div style={{ fontSize: 11.5, color: "var(--ink-3)", lineHeight: 1.5 }}>
                  Remainder of {Math.max(0, n - leftEnd - rightEnd)} split across the two long sides.
                </div>
              </>
            )}

            {rectInvalid && (
              <div style={{
                fontSize: 12, color: "var(--warn-ink)",
                background: "var(--warn-soft)",
                border: "1px solid var(--warn-line)",
                borderRadius: 6, padding: "8px 10px",
              }}>
                Ends total {leftEnd + rightEnd} exceeds group size {n}.
              </div>
            )}
          </div>

          {/* Preview */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            border: "1px solid var(--line)", borderRadius: 10,
            background: "var(--paper-1)",
            padding: 16, minHeight: 340,
          }}>
            {n === 0 ? (
              <div style={{ color: "var(--ink-4)", fontStyle: "italic", fontSize: 13 }}>
                Add people to this group to preview seating.
              </div>
            ) : rectInvalid ? (
              <div style={{ color: "var(--ink-3)", fontStyle: "italic", fontSize: 13 }}>
                Adjust end counts to preview.
              </div>
            ) : (
              <SeatingDiagram
                shape={shape}
                leftEnd={shape === "rect" ? leftEnd : 0}
                rightEnd={shape === "rect" ? rightEnd : 0}
                people={group.people}
                host={group.host}
                seat1Index={clampedSeat1}
                onSeatClick={onSeatClick}
                size={420}
                showNames
                peopleMap={peopleMap}
              />
            )}
          </div>
        </div>

        <div style={{
          display: "flex", alignItems: "center", gap: 10,
          marginTop: 24, paddingTop: 18, borderTop: "1px solid var(--line)",
        }}>
          {initial && (
            <Btn variant="danger" size="sm" onClick={clear} disabled={busy}>
              Remove seating
            </Btn>
          )}
          <div style={{ flex: 1 }} />
          <Btn variant="ghost" size="sm" onClick={onClose} disabled={busy}>Cancel</Btn>
          <Btn
            variant="primary"
            size="sm"
            icon={I.check}
            onClick={save}
            disabled={busy || !config}
          >
            Save seating
          </Btn>
        </div>
      </div>
    </div>
  );
}

function ShapeOption({ label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        flex: 1,
        padding: "8px 10px",
        borderRadius: 8,
        border: `1px solid ${active ? "var(--ink)" : "var(--line-2)"}`,
        background: active ? "var(--ink)" : "transparent",
        color: active ? "var(--paper-0)" : "var(--ink)",
        fontSize: 12,
        fontWeight: 500,
        fontFamily: "var(--ui)",
        cursor: "pointer",
        transition: "background 0.12s, color 0.12s, border-color 0.12s",
      }}
    >
      {label}
    </button>
  );
}
